import { defineComponent as _defineComponent } from 'vue'
import { trackEvent } from '@@/bits/analytics'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { isRegistered } from '@@/bits/user_model'
import { defineAsyncComponent } from '@@/bits/vue'
import { openUrlInBrowserFromZoom } from '@@/bits/zoom'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import type { PopoverAnchor } from '@@/library/v4/components/OzPopoverModal.vue'
import { getAnchorFromElement } from '@@/library/v4/components/OzPopoverModal.vue'
import SearchIconWithIndicator from '@@/library/v4/components/SearchIconWithIndicator.vue'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useSlideshowStore } from '@@/pinia/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAccountsStore } from '@@/pinia/surface_accounts_store'
import { useSurfaceActivityPanelStore } from '@@/pinia/surface_activity_panel'
import { useSurfaceAIChatStore } from '@@/pinia/surface_ai_chat_store'
import { useSurfaceFilterStore } from '@@/pinia/surface_filter'
import { useSurfaceFreezePadletPanel } from '@@/pinia/surface_freeze_padlet_panel'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostSearchStore } from '@@/pinia/surface_post_search'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceRemakePanelStore } from '@@/pinia/surface_remake_panel'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceSharePanelStore } from '@@/pinia/surface_share_panel'
import { useWindowSizeStore } from '@@/pinia/window_size'
import AppBarAccountAvatar from '@@/vuecomponents/AppBarAccountAvatar.vue'
import MagicChatIconButtonVue from '@@/vuecomponents/MagicChatIconButton.vue'
import type { Button } from '@@/vuecomponents/SurfaceAddPostMenu.vue'
import SurfaceAddPostMenu from '@@/vuecomponents/SurfaceAddPostMenu.vue'
import SurfaceGuestIdLabel from '@@/vuecomponents/SurfaceGuestIdLabel.vue'
import { useSurfaceButtonSkinBasedTwClasses } from '@@/vuecomposables/surface_button_skin_based_tw_classes'
import useZoomCollaboration from '@@/vuecomposables/zoom_collaboration'
import type { LongPressEvent } from '@@/vuedirectives/emit_long_press'
import vEmitLongPress from '@@/vuedirectives/emit_long_press'
import { debounce } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfaceActionBar',
  setup(__props) {

const SurfaceActionBarMoreMenu = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceActionBarMoreMenu.vue'))

const POPOVER_WIDTH = 256
const ONBOARDING_PANEL_OFFSET = POPOVER_WIDTH + 13
const moreButton = ref<InstanceType<typeof OzIconButton>>()
const isElectronApp = device.electronApp

const surfaceStore = useSurfaceStore()
const surfaceAccountsStore = useSurfaceAccountsStore()
const surfaceGuestStore = useSurfaceGuestStore()
const {
  user,
  isMap,
  links,
  loginAndRedirectToWallUrl,
  isDesktopLayout,
  isMobileLayout,
  skinPrimaryVariantColorAsRGB,
  skinAccentColorAsRGB,
  skinAccentTextColor,
  isSkinColorAvailable,
  buttonDarkMode,
  xActionBarMoreMenu,
  isSectionBreakout,
  wallAttributes,
  isFrozen,
  hasSidePanelOutsideSurface,
  isLibraryWall,
  isMagicWall,
} = storeToRefs(surfaceStore)
const {
  isCurrentLibrarySchool,
  isCurrentLibraryClassroom,
  isLibraryOwner,
  isLibraryAdmin,
  isLibraryTeacher,
  isTeacher,
  isAdmin,
  isOwner,
  isNativeAccount,
  isBackpack,
} = storeToRefs(surfaceAccountsStore)
const { showActionBarMoreMenu, hideActionBarMoreMenu, showRemakePanelOld, showPromptToLoginDialog } = surfaceStore
const surfacePermissionsStore = useSurfacePermissionsStore()
const { canIAdminister, canIPost, canIRemake, amIOwner } = storeToRefs(surfacePermissionsStore)

const { toggleSettingsPanel } = useSurfaceSettingsStore()
const surfaceActivityStore = useSurfaceActivityPanelStore()
const { xActivityPanel } = storeToRefs(surfaceActivityStore)
const { showActivityPanel, closeActivityPanel } = surfaceActivityStore

const { showRemakePanel } = useSurfaceRemakePanelStore()
const { startPickingLocation } = useSurfaceMapStore()
const { startNewPost } = useSurfacePostsStore()
const { showSurfaceFreezePadletPanel } = useSurfaceFreezePadletPanel()
const { coverPageUrl } = storeToRefs(useSlideshowStore())

const { isSmallerThanTabletPortrait, isSmallerThanTabletLandscape } = storeToRefs(useWindowSizeStore())
const surfaceAIChatStore = useSurfaceAIChatStore()
const { toggleChatPanel } = surfaceAIChatStore
const { xSurfaceAIChatPanel, magicWallFirstOpen } = storeToRefs(surfaceAIChatStore)

const { isRemakeable } = storeToRefs(useSurfaceSettingsStore())

const { buttonSkinBasedTailwindClasses, buttonSkinBasedTailwindClassesWithoutHover } =
  useSurfaceButtonSkinBasedTwClasses()

const expandedPostStore = useExpandedPostStore()
const { xExpandedPostPanel } = storeToRefs(expandedPostStore)

const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
const { isDemoPadletPanel, isDemoPadletPanelDesktop, demoPadletPanelVisibility, isShareStep } =
  storeToRefs(surfaceDemoPadletPanelStore)

const isDemoPadletPanelDesktopAndShareStep = computed<boolean>(
  () => isDemoPadletPanelDesktop.value && isShareStep.value,
)

const useSkinBasedColorsForButtons = computed<boolean>(() => {
  return isSkinColorAvailable.value && !isMobileLayout.value
})

const buttonColorScheme = computed(() => {
  if (isSectionBreakout.value) {
    return OzIconButtonColorScheme.Transparent
  }

  if (useSkinBasedColorsForButtons.value) {
    return OzIconButtonColorScheme.Bare
  }

  return isMobileLayout.value ? OzIconButtonColorScheme.SecondaryClear : OzIconButtonColorScheme.Secondary
})

const activeButtonColorScheme = computed<OzIconButtonColorScheme>(() => {
  if (useSkinBasedColorsForButtons.value) {
    return OzIconButtonColorScheme.Bare
  }

  return isMobileLayout.value ? OzIconButtonColorScheme.SecondaryClearActive : OzIconButtonColorScheme.SecondaryActive
})

const activeButtonSkinBasedTailwindClasses = computed(() => {
  if (!useSkinBasedColorsForButtons.value) {
    return {}
  }

  // buttonDarkMode is either true or false here (cannot be 'auto') since we checked for isSkinColorAvailable
  return {
    // Base
    'bg-grey-1000 bg-opacity-5': buttonDarkMode.value === false,
    'bg-white-100 bg-opacity-10': buttonDarkMode.value === true,
    'text-grape-500': buttonDarkMode.value === false,
    'text-canary-500': buttonDarkMode.value === true,

    // Hovered
    'hover-hover:hover:bg-opacity-10': buttonDarkMode.value === false,
    'hover-hover:hover:bg-opacity-15': buttonDarkMode.value === true,

    // Disabled
    'aria-disabled:!bg-highway-400': buttonDarkMode.value === false,
    'aria-disabled:!bg-white-disabled': buttonDarkMode.value === true,
  }
})

const isUserRegistered = computed<boolean>(() => {
  return isRegistered(user.value)
})

const isRemakeDisabled = computed<boolean>(() => {
  if (!isUserRegistered.value) {
    // Check isRemakeable as canIRemake is always false for unregistered users
    // and we want the remake button to be clickable (for padlets with remakes enabled)
    // so the login dialog can be shown
    return !isRemakeable.value
  }
  return !canIRemake.value
})

/**
 * MOBILE
 * - [ Share, Activity, +, Search, ... ]
 *
 * MOBILE ZOOM APP
 * - [ Slideshow, Activity, +, Search, Open in browser ]
 *
 *
 * DESKTOP
 * - [ Share, Remake, Activity, Slideshow, Settings, ... ]
 *
 * DESKTOP ZOOM APP
 * - [ Activity, Slideshow, Settings, Open in browser ]
 */

const { searchTerm } = storeToRefs(useSurfacePostSearchStore())
const { hasSelectedFilters } = storeToRefs(useSurfaceFilterStore())

const xSearchIndicator = computed<boolean>(() => {
  return searchTerm.value !== '' || hasSelectedFilters.value
})

function showSharePanel() {
  if (isDemoPadletPanelDesktopAndShareStep.value) {
    surfaceDemoPadletPanelStore.handleStartShareAnimation()
  } else {
    useSurfaceSharePanelStore().xSurfaceSharePanel = true
  }
}

function showSearchBar() {
  useSurfaceStore().openSearchBar()
}

async function openPadletInBrowser() {
  openUrlInBrowserFromZoom(links.value.show)
}

function openSlideshow() {
  trackEvent('Surface', 'Opened slideshow')
  if (!links.value.show) {
    return
  }

  if (device.zoomApp) {
    navigateTo(coverPageUrl.value)
    return
  }

  navigateTo(coverPageUrl.value, { target: '_blank' })
}

function showRequestLoginToRemakeDialog(): void {
  showPromptToLoginDialog({
    title: __('You must be logged in to remake this padlet'),
  })
}

function handleRemakeClick(): void {
  if (isUserRegistered.value && isAppUsing('remakePanelV2')) {
    showRemakePanel()
  } else if (isUserRegistered.value && !isAppUsing('remakePanelV2')) {
    showRemakePanelOld()
  } else if (!isUserRegistered.value) {
    showRequestLoginToRemakeDialog()
  }
}

const isLoginAndRedirectToWallUrlExpired = ref(false)

/**
 * ZOOM
 */
const { isZoomCollaborationGuest } = useZoomCollaboration()

/**
 * SHOW/HIDE MORE MENU
 */

function openMoreMenu(): void {
  if (!moreMenuAnchor.value.top || !moreMenuAnchor.value.left) {
    updateMoreMenuAnchor()
  }
  showActionBarMoreMenu()
}

function closeMoreMenu(): void {
  hideActionBarMoreMenu()
}

/**
 * MORE MENU POSITIONING
 */

const moreMenuAnchor = ref<Partial<PopoverAnchor>>({})

function updateMoreMenuAnchor(): void {
  const moreButtonEl = moreButton.value?.$el
  if (!moreButtonEl) return
  const anchor = getAnchorFromElement(moreButtonEl, {
    top: 0,
    left: isMobileLayout.value
      ? dir() === 'rtl'
        ? -15
        : hasSidePanelOutsideSurface.value
        ? 12 - 160 // numbers by experimentation
        : 12
      : dir() === 'rtl'
      ? hasSidePanelOutsideSurface.value
        ? -12 + ONBOARDING_PANEL_OFFSET
        : -12
      : hasSidePanelOutsideSurface.value
      ? -39 - ONBOARDING_PANEL_OFFSET
      : -39,
  })
  moreMenuAnchor.value = {
    ...anchor,
    position: 'side',
  }
}

const debouncedUpdateMoreMenuAnchor = debounce(() => {
  if (window.visualViewport && window.visualViewport.scale > 1) {
    // When the content is pinch-zoomed, prevented any resizing and recalculation.
    return
  }
  updateMoreMenuAnchor()
}, 500)

const addPostButtonRef = ref<InstanceType<typeof OzBaseButton>>()
const xAddPostMenu = ref(false)

function handleAddPostClick(): void {
  if (isMap.value) {
    startPickingLocation()
  } else {
    startNewPost({})
  }
}

function handleAddPostLongPress(e: LongPressEvent): void {
  e.preventDefault()
  if (!showSurfaceChat.value) return
  xAddPostMenu.value = true
}

const addPostMenuButtons: Button[] = [
  {
    text: __('Write a post'),
    ariaLabel: __('Write a post'),
    icon: 'pencil_outline',
    action: () => {
      startNewPost({})
    },
  },
  {
    text: __('Teaching assistant'),
    ariaLabel: __('Teaching assistant'),
    icon: 'sparkles_outline',
    action: () => {
      surfaceAIChatStore.toggleChatPanel()
    },
  },
]

const showSurfaceChat = computed(() => {
  const isSurfaceChatViewable =
    isAppUsing('surfaceAiChat') && amIOwner.value && demoPadletPanelVisibility.value.showSurfaceAiChat

  if (!isSurfaceChatViewable) {
    return false
  }

  const isEducationalLibrary = computed(
    (): boolean => isLibraryWall.value && (isCurrentLibraryClassroom.value || isCurrentLibrarySchool.value),
  )
  const isLibraryTeacherOrAbove = computed(
    (): boolean =>
      isEducationalLibrary.value && (isLibraryOwner.value || isLibraryAdmin.value || isLibraryTeacher.value),
  )
  const isBackpackTeacherOrAbove = computed(
    (): boolean => isBackpack.value && (isTeacher.value || isAdmin.value || isOwner.value),
  )

  if (isMagicWall.value && (isLibraryTeacherOrAbove.value || isBackpackTeacherOrAbove.value)) {
    return true
  }

  const isNativeAdmin = computed((): boolean => isNativeAccount.value && isAdmin.value)
  if (isNativeAdmin.value) {
    return true
  }

  return false
})

/**
 * COMPONENT LIFECYCLE
 */

onMounted(async () => {
  if (loginAndRedirectToWallUrl.value) {
    // Refer to DEEPLINK_RESPONSE_WINDOW_DURATION_WITH_BUFFER in app/services/lti_launch_context.rb
    const TIMEOUT_MS = 15 * 60 * 1000
    setTimeout(() => (isLoginAndRedirectToWallUrlExpired.value = true), TIMEOUT_MS)
  }
  window.addEventListener('resize', debouncedUpdateMoreMenuAnchor)
  await surfaceAccountsStore.initialize()

  surfaceAIChatStore.loadMagicWallFirstOpen() // Load data to show feedback button, remove wall options if first time
  if (showSurfaceChat.value && magicWallFirstOpen.value) surfaceAIChatStore.showChatPanel() // without having to press button to bring it up
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', debouncedUpdateMoreMenuAnchor)
})

return { __sfc: true,SurfaceActionBarMoreMenu, POPOVER_WIDTH, ONBOARDING_PANEL_OFFSET, moreButton, isElectronApp, surfaceStore, surfaceAccountsStore, surfaceGuestStore, user, isMap, links, loginAndRedirectToWallUrl, isDesktopLayout, isMobileLayout, skinPrimaryVariantColorAsRGB, skinAccentColorAsRGB, skinAccentTextColor, isSkinColorAvailable, buttonDarkMode, xActionBarMoreMenu, isSectionBreakout, wallAttributes, isFrozen, hasSidePanelOutsideSurface, isLibraryWall, isMagicWall, isCurrentLibrarySchool, isCurrentLibraryClassroom, isLibraryOwner, isLibraryAdmin, isLibraryTeacher, isTeacher, isAdmin, isOwner, isNativeAccount, isBackpack, showActionBarMoreMenu, hideActionBarMoreMenu, showRemakePanelOld, showPromptToLoginDialog, surfacePermissionsStore, canIAdminister, canIPost, canIRemake, amIOwner, toggleSettingsPanel, surfaceActivityStore, xActivityPanel, showActivityPanel, closeActivityPanel, showRemakePanel, startPickingLocation, startNewPost, showSurfaceFreezePadletPanel, coverPageUrl, isSmallerThanTabletPortrait, isSmallerThanTabletLandscape, surfaceAIChatStore, toggleChatPanel, xSurfaceAIChatPanel, magicWallFirstOpen, isRemakeable, buttonSkinBasedTailwindClasses, buttonSkinBasedTailwindClassesWithoutHover, expandedPostStore, xExpandedPostPanel, surfaceDemoPadletPanelStore, isDemoPadletPanel, isDemoPadletPanelDesktop, demoPadletPanelVisibility, isShareStep, isDemoPadletPanelDesktopAndShareStep, useSkinBasedColorsForButtons, buttonColorScheme, activeButtonColorScheme, activeButtonSkinBasedTailwindClasses, isUserRegistered, isRemakeDisabled, searchTerm, hasSelectedFilters, xSearchIndicator, showSharePanel, showSearchBar, openPadletInBrowser, openSlideshow, showRequestLoginToRemakeDialog, handleRemakeClick, isLoginAndRedirectToWallUrlExpired, isZoomCollaborationGuest, openMoreMenu, closeMoreMenu, moreMenuAnchor, updateMoreMenuAnchor, debouncedUpdateMoreMenuAnchor, addPostButtonRef, xAddPostMenu, handleAddPostClick, handleAddPostLongPress, addPostMenuButtons, showSurfaceChat, device, isAppUsing, __, OzBaseButton, OzIcon, OzIconButton, OzIconButtonColorScheme, OzIconButtonSizePreset, OzPlainButton, OzPlainButtonColorScheme, OzPlainButtonSizePreset, SearchIconWithIndicator, AppBarAccountAvatar, MagicChatIconButtonVue, SurfaceAddPostMenu, SurfaceGuestIdLabel, vEmitLongPress }
}

})